import {
  Building2, CheckCircle, MapPin, Mail 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  FormModal, FormField, FormInput, FormSelect 
} from '@/components/ui/form-modal';
import {
  SITE_STATUS_COLORS, SITE_TYPE_LABELS 
} from '@/features/settings';
import type {
  Site, SiteStatus 
} from '@/features/settings';
import {
  SITE_TYPE_OPTIONS, TIMEZONE_OPTIONS, CURRENCY_OPTIONS 
} from './settings-constants';

interface SiteFormData {
  name: string;
  code: string;
  type: string;
  timezone: string;
  currency: string;
}

interface SitesViewProps {
  readonly sites: Site[];
  readonly isLoading: boolean;
  readonly siteStats: {
    total: number;
    active: number;
    totalVenues: number 
  };
  readonly siteStatusFilter: SiteStatus | undefined;
  readonly setSiteStatusFilter: (status: SiteStatus | undefined) => void;
  readonly selectedSites: string[];
  readonly toggleSiteSelection: (siteId: string) => void;
  readonly showSiteModal: boolean;
  readonly setShowSiteModal: (show: boolean) => void;
  readonly siteFormData: SiteFormData;
  readonly setSiteFormData: (data: SiteFormData) => void;
  readonly onAddSite: () => void;
  readonly onSaveSite: () => void;
  readonly onDeleteSites: () => void;
}

export function SitesView({
  sites,
  isLoading,
  siteStats,
  siteStatusFilter,
  setSiteStatusFilter,
  selectedSites,
  toggleSiteSelection,
  showSiteModal,
  setShowSiteModal,
  siteFormData,
  setSiteFormData,
  onAddSite,
  onSaveSite,
  onDeleteSites,
}: SitesViewProps) {
  const siteExportData = sites.map((s) => ({
    name: s.name,
    code: s.code,
    type: s.type,
    status: s.status,
    timezone: s.timezone,
    currency: s.currency,
    venueCount: s.venues.length,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Site"
        entityNamePlural="Sites"
        selectedCount={selectedSites.length}
        onAdd={onAddSite}
        onDelete={onDeleteSites}
        exportData={siteExportData}
        exportFilename="sites.csv"
        canEdit={false}
        canBulkUpload={false}
      />

      <FormModal
        title="Add Site"
        description="Create a new site location"
        isOpen={showSiteModal}
        onClose={() => setShowSiteModal(false)}
        onSave={onSaveSite}
        saveLabel="Create Site"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Site Name" required>
            <FormInput
              value={siteFormData.name}
              onChange={(e) => setSiteFormData({
                ...siteFormData,
                name: e.target.value 
              })}
              placeholder="MS Celebration"
            />
          </FormField>
          <FormField label="Site Code" required>
            <FormInput
              value={siteFormData.code}
              onChange={(e) => setSiteFormData({
                ...siteFormData,
                code: e.target.value 
              })}
              placeholder="CELEB"
            />
          </FormField>
          <FormField label="Type">
            <FormSelect
              value={siteFormData.type}
              onChange={(e) => setSiteFormData({
                ...siteFormData,
                type: e.target.value 
              })}
              options={SITE_TYPE_OPTIONS}
            />
          </FormField>
          <FormField label="Timezone" required>
            <FormSelect
              value={siteFormData.timezone}
              onChange={(e) => setSiteFormData({
                ...siteFormData,
                timezone: e.target.value 
              })}
              placeholder="Select timezone..."
              options={TIMEZONE_OPTIONS}
            />
          </FormField>
          <FormField label="Currency">
            <FormSelect
              value={siteFormData.currency}
              onChange={(e) => setSiteFormData({
                ...siteFormData,
                currency: e.target.value 
              })}
              options={CURRENCY_OPTIONS}
            />
          </FormField>
        </div>
      </FormModal>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Sites</CardTitle>
            <Building2 className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{siteStats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{siteStats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Venues</CardTitle>
            <MapPin className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{siteStats.totalVenues}</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2">
        {(['active', 'inactive', 'maintenance'] as SiteStatus[]).map((status) => (
          <Button
            key={status}
            variant={siteStatusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSiteStatusFilter(siteStatusFilter === status ? undefined : status)}
            className="capitalize"
          >
            {status}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`site-skeleton-${i.toString()}`} className="h-48 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {sites.map((site) => (
            <SiteCard
              key={site.siteId}
              site={site}
              isSelected={selectedSites.includes(site.siteId)}
              onToggle={() => toggleSiteSelection(site.siteId)}
            />
          ))}
        </div>
      )}
    </>
  );
}

interface SiteCardProps {
  readonly site: Site;
  readonly isSelected: boolean;
  readonly onToggle: () => void;
}

function SiteCard({
  site, isSelected, onToggle 
}: SiteCardProps) {
  return (
    <Card
      className={`cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-primary shadow-md' : 'hover:shadow-md'
      }`}
      onClick={onToggle}
    >
      {isSelected && (
        <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary flex items-center justify-center z-10">
          <span className="text-xs text-primary-foreground">✓</span>
        </div>
      )}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base font-medium">{site.name}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {site.code} • {SITE_TYPE_LABELS[site.type]}
            </p>
          </div>
          <span className={`rounded-full px-2 py-1 text-xs font-medium capitalize ${SITE_STATUS_COLORS[site.status]}`}>
            {site.status}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">Timezone:</span>
          <span>{site.timezone}</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">Currency:</span>
          <span>{site.currency}</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">Languages:</span>
          <span>{site.supportedLanguages.join(', ').toUpperCase()}</span>
        </div>
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Venues ({site.venues.length}):</p>
          <div className="flex flex-wrap gap-1">
            {site.venues.map((venue) => (
              <span
                key={venue.venueId}
                className={`rounded px-2 py-0.5 text-xs ${
                  venue.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}
              >
                {venue.name}
              </span>
            ))}
          </div>
        </div>
        {site.contactEmail && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
            <Mail className="h-3 w-3" /> {site.contactEmail}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
